/**
 * 궁합 페이지
 */
import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { usePricing } from '../../contexts/PricingContext';
import { fortuneAPI } from '../../services/api';
import { tokens } from '../../design-system/tokens';
import { Button } from '../../components/atoms/Button';
import { Input } from '../../components/atoms/Input';
import { BirthDateInput } from '../../components/atoms/BirthDateInput';
import { LoadingSpinner } from '../../components/atoms/LoadingSpinner';
import { PaymentRequired } from '../../components/organisms/PaymentRequired';
import { convertBoldMarkdown } from '../../utils/textFormat';

const Container = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: ${tokens.spacing[10]} ${tokens.spacing[5]};
  overflow-x: hidden;
  box-sizing: border-box;

  @media (max-width: ${tokens.breakpoints.md}) {
    padding: ${tokens.spacing[8]} ${tokens.spacing[4]};
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    padding: ${tokens.spacing[6]} ${tokens.spacing[4]};
  }
`;

const Title = styled.h1`
  font-size: ${tokens.typography.fontSize['3xl']};
  font-weight: ${tokens.typography.fontWeight.bold};
  color: ${tokens.colors.neutral[800]};
  margin-bottom: ${tokens.spacing[8]};
  text-align: center;
  word-break: keep-all;

  @media (max-width: ${tokens.breakpoints.md}) {
    font-size: ${tokens.typography.fontSize['2xl']};
    margin-bottom: ${tokens.spacing[6]};
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    font-size: ${tokens.typography.fontSize.xl};
    margin-bottom: ${tokens.spacing[5]};
  }
`;

const Form = styled.form`
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacing[5]};
  background: ${tokens.colors.neutral[0]};
  padding: ${tokens.spacing[8]};
  border-radius: ${tokens.borderRadius.md};
  box-shadow: ${tokens.shadows.base};
  box-sizing: border-box;

  @media (max-width: ${tokens.breakpoints.md}) {
    padding: ${tokens.spacing[6]};
    gap: ${tokens.spacing[4]};
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    padding: ${tokens.spacing[5]};
    gap: ${tokens.spacing[4]};
  }
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: ${tokens.typography.fontWeight.medium};
  color: ${tokens.colors.neutral[700]};
  margin-bottom: 8px;
`;

const ResultContainer = styled.div`
  margin-top: ${tokens.spacing[8]};
  padding: ${tokens.spacing[6]} ${tokens.spacing[5]};
  background: ${tokens.colors.neutral[0]};
  border-radius: ${tokens.borderRadius.lg};
  border: 1px solid ${tokens.colors.neutral[200]};
  box-shadow: ${tokens.shadows.md};

  @media (max-width: ${tokens.breakpoints.md}) {
    margin-top: ${tokens.spacing[6]};
    padding: ${tokens.spacing[5]} ${tokens.spacing[4]};
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    margin-top: ${tokens.spacing[5]};
    padding: ${tokens.spacing[4]} ${tokens.spacing[3]};
  }
`;

const ResultHTML = styled.div`
  font-family: ${tokens.typography.fontFamily.primary} !important;
  color: ${tokens.colors.neutral[700]};
  line-height: ${tokens.typography.lineHeight.relaxed};
  word-break: keep-all;

  * {
    font-family: ${tokens.typography.fontFamily.primary} !important;
  }

  h1 {
    font-family: ${tokens.typography.fontFamily.primary} !important;
    font-size: ${tokens.typography.fontSize['2xl']};
    font-weight: ${tokens.typography.fontWeight.bold};
    color: ${tokens.colors.neutral[800]};
    margin-bottom: ${tokens.spacing[6]};
    margin-top: 0;

    @media (max-width: ${tokens.breakpoints.md}) {
      font-size: ${tokens.typography.fontSize.xl};
      margin-bottom: ${tokens.spacing[5]};
    }

    @media (max-width: ${tokens.breakpoints.sm}) {
      font-size: ${tokens.typography.fontSize.lg};
      margin-bottom: ${tokens.spacing[4]};
    }
  }

  h2 {
    font-family: ${tokens.typography.fontFamily.primary} !important;
    font-size: ${tokens.typography.fontSize.xl};
    font-weight: ${tokens.typography.fontWeight.semibold};
    color: ${tokens.colors.neutral[800]};
    margin-top: ${tokens.spacing[8]};
    margin-bottom: ${tokens.spacing[4]};
    padding-bottom: ${tokens.spacing[3]};
    border-bottom: 2px solid ${tokens.colors.neutral[200]};

    @media (max-width: ${tokens.breakpoints.md}) {
      font-size: ${tokens.typography.fontSize.lg};
      margin-top: ${tokens.spacing[6]};
    }

    @media (max-width: ${tokens.breakpoints.sm}) {
      font-size: ${tokens.typography.fontSize.base};
      margin-top: ${tokens.spacing[5]};
    }
  }

  h3 {
    font-family: ${tokens.typography.fontFamily.primary} !important;
    font-size: ${tokens.typography.fontSize.lg};
    font-weight: ${tokens.typography.fontWeight.semibold};
    color: ${tokens.colors.neutral[700]};
    margin-top: ${tokens.spacing[6]};
    margin-bottom: ${tokens.spacing[3]};

    @media (max-width: ${tokens.breakpoints.md}) {
      font-size: ${tokens.typography.fontSize.base};
    }

    @media (max-width: ${tokens.breakpoints.sm}) {
      font-size: ${tokens.typography.fontSize.sm};
    }
  }

  p {
    font-size: ${tokens.typography.fontSize.base};
    margin-bottom: ${tokens.spacing[4]};
    line-height: ${tokens.typography.lineHeight.relaxed};

    @media (max-width: ${tokens.breakpoints.sm}) {
      font-size: ${tokens.typography.fontSize.sm};
    }
  }

  ul, ol {
    padding-left: ${tokens.spacing[6]};
    margin-bottom: ${tokens.spacing[4]};

    @media (max-width: ${tokens.breakpoints.sm}) {
      padding-left: ${tokens.spacing[5]};
    }
  }

  li {
    margin-bottom: ${tokens.spacing[2]};
    line-height: ${tokens.typography.lineHeight.relaxed};
  }

  strong, b {
    font-weight: ${tokens.typography.fontWeight.semibold};
    color: ${tokens.colors.primary[600]};
  }

  em, i {
    font-style: italic;
    color: ${tokens.colors.neutral[600]};
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: ${tokens.spacing[6]} 0;
    font-size: ${tokens.typography.fontSize.sm};

    @media (max-width: ${tokens.breakpoints.sm}) {
      font-size: ${tokens.typography.fontSize.xs};
      display: block;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
    }
  }

  th {
    background: ${tokens.colors.neutral[100]};
    padding: ${tokens.spacing[3]} ${tokens.spacing[2]};
    text-align: left;
    font-weight: ${tokens.typography.fontWeight.semibold};
    border-bottom: 2px solid ${tokens.colors.neutral[300]};
  }

  td {
    padding: ${tokens.spacing[3]} ${tokens.spacing[2]};
    border-bottom: 1px solid ${tokens.colors.neutral[200]};
  }

  tr:nth-child(even) td {
    background: ${tokens.colors.neutral[50]};
  }

  blockquote {
    border-left: 4px solid ${tokens.colors.primary[500]};
    padding-left: ${tokens.spacing[4]};
    margin: ${tokens.spacing[6]} 0;
    font-style: italic;
    color: ${tokens.colors.neutral[600]};
  }

  hr {
    border: none;
    border-top: 1px solid ${tokens.colors.neutral[200]};
    margin: ${tokens.spacing[8]} 0;
  }

  div[style*="background"] {
    border-radius: ${tokens.borderRadius.md};
    padding: ${tokens.spacing[4]};
    margin: ${tokens.spacing[4]} 0;
  }

  section {
    margin-bottom: ${tokens.spacing[6]};

    &:last-child {
      margin-bottom: 0;
    }
  }
`;


const ErrorMessage = styled.div`
  padding: 15px;
  background: #fee;
  color: #c00;
  border-radius: 8px;
  margin-top: 15px;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: ${tokens.typography.fontWeight.semibold};
  color: ${tokens.colors.neutral[700]};
  margin: 20px 0 10px 0;
`;

export const CompatibilityPage: React.FC = () => {
  const { user, checkServiceAccess } = useAuth();
  const { servicePrices } = usePricing();
  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [partnerName, setPartnerName] = useState('');
  const [partnerBirthDate, setPartnerBirthDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response: any = await fortuneAPI.compatibility({ name, birthDate, partnerName, partnerBirthDate });
      if (response.success) {
        setResult(response.data);
      }
    } catch (err: any) {
      setError(err.message || '궁합 분석 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return <Container><Title>로그인이 필요합니다</Title></Container>;
  }

  // 결제 확인
  if (!checkServiceAccess('compatibility')) {
    return (
      <PaymentRequired
        serviceName="궁합"
        productType="compatibility"
        oneTimePrice={servicePrices.compatibility}
        oneTimeDescription="두 사람의 궁합을 봅니다"
        oneTimeFeatures={['1회 이용', '사랑 궁합', '결혼 궁합']}
      />
    );
  }

  return (
    <Container>
      <Title>💑 궁합</Title>

      <Form onSubmit={handleSubmit}>
        <SectionTitle>본인 정보</SectionTitle>
        <div>
          <Label>이름</Label>
          <Input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div>
          <BirthDateInput
            label="생년월일"
            value={birthDate}
            onChange={setBirthDate}
            
            required
          />
        </div>

        <SectionTitle>상대방 정보</SectionTitle>
        <div>
          <Label>이름</Label>
          <Input type="text" value={partnerName} onChange={(e) => setPartnerName(e.target.value)} required />
        </div>
        <div>
          <BirthDateInput
            label="생년월일"
            value={partnerBirthDate}
            onChange={setPartnerBirthDate}
            
            required
          />
        </div>

        <Button type="submit" disabled={loading}>
          {loading ? '분석 중...' : '궁합 보기'}
        </Button>

        {error && <ErrorMessage>{error}</ErrorMessage>}
      </Form>

      {loading && <LoadingSpinner message="AI가 궁합을 분석하고 있습니다" />}

      {result && (
        <ResultContainer>
          <ResultHTML dangerouslySetInnerHTML={{ __html: convertBoldMarkdown(result.html) }} />
        </ResultContainer>
      )}
    </Container>
  );
};